import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="oms--oms--row-actions-menu"
export default class extends Controller {
  static targets = ["menu"]
  static values = { open: Boolean }

  connect() {
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.closeOnScroll = this.closeOnScroll.bind(this)
  }

  disconnect() {
    this.removeEventListeners()
  }

  toggleMenu(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.openValue) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  openMenu() {
    // Close any other open menus first
    this.closeAllMenus()

    this.openValue = true
    this.menuTarget.classList.remove("hidden")
    this.addEventListeners()

    // Position the dropdown to avoid viewport overflow
    this.positionDropdown()

    // Set focus to first menu item
    const firstMenuItem = this.menuTarget.querySelector('[role="menuitem"]')
    if (firstMenuItem) {
      firstMenuItem.focus()
    }
  }

  positionDropdown() {
    const button = this.element.querySelector('button')
    const menu = this.menuTarget
    const buttonRect = button.getBoundingClientRect()
    
    menu.style.top = ''
    menu.style.left = ''
    menu.style.right = ''
    menu.style.bottom = ''
    menu.classList.remove('origin-top-left', 'origin-bottom-left', 'origin-top-right', 'origin-bottom-right')
    
    const menuItems = menu.querySelectorAll('[role="menuitem"], .cursor-not-allowed')
    const estimatedMenuHeight = menuItems.length * 40 + 16 // 40px per item + 16px padding
    const estimatedMenuWidth = 224 // w-56 = 14rem = 224px
    
    const spaceBelow = window.innerHeight - buttonRect.bottom - 8 // 8px margin
    const spaceAbove = buttonRect.top - 8 // 8px margin
    const spaceToRight = window.innerWidth - buttonRect.right - 8 // 8px margin
    const spaceToLeft = buttonRect.left - 8 // 8px margin
    
    let top, bottom
    if (spaceBelow >= estimatedMenuHeight) {
      top = buttonRect.bottom - 4 
      menu.classList.add('origin-top-left')
    } else if (spaceAbove >= estimatedMenuHeight) {
      bottom = window.innerHeight - buttonRect.top - 4 
      menu.classList.add('origin-bottom-left')
    } else {
      top = buttonRect.bottom - 4
      menu.classList.add('origin-top-left')
      menu.style.maxHeight = `${spaceBelow - 16}px`
      menu.style.overflowY = 'auto'
    }
    
    let left, right
    if (spaceToRight >= estimatedMenuWidth) {
      left = buttonRect.left
    } else if (spaceToLeft >= estimatedMenuWidth) {
      right = window.innerWidth - buttonRect.right
    } else {
      left = Math.max(8, Math.min(buttonRect.left, window.innerWidth - estimatedMenuWidth - 8))
    }
    
    if (top !== undefined) {
      menu.style.top = `${top}px`
    }
    if (bottom !== undefined) {
      menu.style.bottom = `${bottom}px`
    }
    if (left !== undefined) {
      menu.style.left = `${left}px`
    }
    if (right !== undefined) {
      menu.style.right = `${right}px`
    }
  }

  closeMenu() {
    this.openValue = false
    this.menuTarget.classList.add("hidden")
    this.menuTarget.style.maxHeight = ''
    this.menuTarget.style.overflowY = ''
    this.removeEventListeners()
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeMenu()
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.closeMenu()
    }
  }

  closeOnScroll(event) {
    this.closeMenu()
  }

  addEventListeners() {
    document.addEventListener("click", this.closeOnClickOutside)
    document.addEventListener("keydown", this.closeOnEscape)
    window.addEventListener("scroll", this.closeOnScroll, true)
  }

  removeEventListeners() {
    document.removeEventListener("click", this.closeOnClickOutside)
    document.removeEventListener("keydown", this.closeOnEscape)
    window.removeEventListener("scroll", this.closeOnScroll, true)
  }

  closeAllMenus() {
    document.querySelectorAll('[data-controller*="oms--oms--row-actions-menu"]').forEach(menu => {
      const controller = this.application.getControllerForElementAndIdentifier(menu, "oms--oms--row-actions-menu")
      if (controller && controller !== this && controller.openValue) {
        controller.closeMenu()
      }
    })
  }
}